import { Injectable } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root'
})
export class TooltipManagerService {
  private activeTooltips: Set<NgbTooltip> = new Set();
  private wrappedTooltips: WeakSet<NgbTooltip> = new WeakSet();
  private pendingTimeouts: Map<NgbTooltip, any> = new Map();

  constructor() {}

  // Register a tooltip when it's created
  register(tooltip: NgbTooltip): void {
    if (this.activeTooltips.has(tooltip)) {
      return; // Already registered
    }
    
    this.activeTooltips.add(tooltip);
    
    // Wrap the open() method to ensure all other tooltips close first
    if (!this.wrappedTooltips.has(tooltip)) {
      const originalOpen = tooltip.open.bind(tooltip);
      tooltip.open = () => {
        // Close all other tooltips before opening this one
        this.closeAllExcept(tooltip);
        return originalOpen();
      };
      this.wrappedTooltips.add(tooltip);
    }
  }

  // Register a pending timeout for a tooltip
  registerTimeout(tooltip: NgbTooltip, timeoutId: any): void {
    // Cancel any existing timeout for this tooltip
    this.cancelTimeout(tooltip);
    this.pendingTimeouts.set(tooltip, timeoutId);
  }

  // Cancel a specific tooltip's timeout
  cancelTimeout(tooltip: NgbTooltip): void {
    const timeoutId = this.pendingTimeouts.get(tooltip);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.pendingTimeouts.delete(tooltip);
    }
  }

  // Cancel all pending timeouts except for one tooltip
  cancelAllTimeoutsExcept(excludeTooltip: NgbTooltip): void {
    this.pendingTimeouts.forEach((timeoutId, tooltip) => {
      if (tooltip !== excludeTooltip) {
        clearTimeout(timeoutId);
        this.pendingTimeouts.delete(tooltip);
      }
    });
  }

  // Cancel all pending timeouts
  cancelAllTimeouts(): void {
    this.pendingTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.pendingTimeouts.clear();
  }

  // Unregister a tooltip when it's destroyed
  unregister(tooltip: NgbTooltip): void {
    this.cancelTimeout(tooltip);
    this.activeTooltips.delete(tooltip);
  }

  // Check if a tooltip is already registered
  isRegistered(tooltip: NgbTooltip): boolean {
    return this.activeTooltips.has(tooltip);
  }

  // Close all open tooltips
  closeAll(): void {
    this.cancelAllTimeouts();
    this.activeTooltips.forEach(tooltip => {
      try {
        if (tooltip.isOpen()) {
          tooltip.close();
        }
      } catch (e) {
        // Tooltip might be destroyed, ignore errors
      }
    });
  }

  // Close all tooltips except the specified one
  closeAllExcept(excludeTooltip: NgbTooltip): void {
    this.cancelAllTimeoutsExcept(excludeTooltip);
    this.activeTooltips.forEach(tooltip => {
      if (tooltip !== excludeTooltip) {
        try {
          if (tooltip.isOpen()) {
            tooltip.close();
          }
        } catch (e) {
          // Tooltip might be destroyed, ignore errors
        }
      }
    });
  }
}